import type { MutationPolicyPreview, MutationStopDraft } from './types';
import { isExecutedStop } from './mutationStopHelpers';

function fieldChanged(a: unknown, b: unknown): boolean {
  return String(a ?? '').trim() !== String(b ?? '').trim();
}

export function buildMutationPolicyPreview(params: {
  baseline: MutationStopDraft[];
  draft: MutationStopDraft[];
}): MutationPolicyPreview {
  const baselineById = new Map(
    params.baseline.filter((s) => s.id).map((s) => [Number(s.id), s])
  );
  const draftIds = new Set(params.draft.filter((s) => s.id).map((s) => Number(s.id)));

  for (const base of params.baseline) {
    if (!base.id) continue;
    if (isExecutedStop(base) && !draftIds.has(Number(base.id))) {
      return {
        status: 'forbidden',
        title: 'Запрещено',
        message: 'Нельзя удалить выполненную точку',
        affectedSubsystems: [],
        requiresConfirmation: false
      };
    }
  }

  for (const stop of params.draft) {
    if (!stop.id) continue;
    const base = baselineById.get(Number(stop.id));
    if (!base) continue;
    if (isExecutedStop(base)) {
      const onlyNotes =
        fieldChanged(base.notes, stop.notes) &&
        !fieldChanged(base.address, stop.address) &&
        !fieldChanged(base.plannedAt, stop.plannedAt) &&
        !fieldChanged(base.city, stop.city) &&
        base.position === stop.position;
      if (!onlyNotes && (fieldChanged(base.address, stop.address) || fieldChanged(base.plannedAt, stop.plannedAt) || base.position !== stop.position)) {
        return {
          status: 'forbidden',
          title: 'Запрещено',
          message: 'Точка уже выполнена',
          affectedSubsystems: [],
          requiresConfirmation: false
        };
      }
    }
  }

  const hasInsert = params.draft.some((s) => !s.id);
  const hasDelete = params.baseline.some((b) => b.id && !isExecutedStop(b) && !draftIds.has(Number(b.id)));
  const hasReorder = params.draft.some((s) => {
    if (!s.id) return false;
    const base = baselineById.get(Number(s.id));
    return base && !isExecutedStop(base) && base.position !== s.position;
  });
  const hasFieldEdit = params.draft.some((s) => {
    if (!s.id) return false;
    const base = baselineById.get(Number(s.id));
    if (!base || isExecutedStop(base)) return false;
    return (
      fieldChanged(base.address, s.address) ||
      fieldChanged(base.plannedAt, s.plannedAt) ||
      fieldChanged(base.city, s.city) ||
      fieldChanged(base.contactPerson, s.contactPerson) ||
      fieldChanged(base.phone, s.phone) ||
      fieldChanged(base.notes, s.notes)
    );
  });

  if (!hasInsert && !hasDelete && !hasReorder && !hasFieldEdit) {
    return {
      status: 'allowed',
      title: 'Разрешено',
      message: null,
      affectedSubsystems: [],
      requiresConfirmation: false
    };
  }

  const affected = new Set<string>();
  if (hasInsert || hasDelete || hasReorder || hasFieldEdit) {
    affected.add('projection');
    affected.add('timeline');
  }
  if (hasInsert || hasDelete || hasReorder) {
    affected.add('execution_graph');
    affected.add('coordination');
    affected.add('occupancy');
  }
  if (hasFieldEdit) {
    affected.add('coordination');
  }

  return {
    status: 'allowed',
    title: 'Разрешено',
    message: null,
    affectedSubsystems: Array.from(affected),
    requiresConfirmation: false
  };
}

export function policyFeedbackFromApi(contract: {
  allowed?: boolean;
  forbidden?: boolean;
  requiresConfirmation?: boolean;
  policyTier?: string;
  affectedSubsystems?: string[];
  rejectedFields?: Array<{ message?: string }>;
} | null | undefined, fallbackMessage?: string): MutationPolicyPreview {
  if (!contract) {
    return {
      status: 'allowed',
      title: 'Разрешено',
      message: fallbackMessage ?? null,
      affectedSubsystems: [],
      requiresConfirmation: false
    };
  }
  if (contract.forbidden || contract.allowed === false) {
    return {
      status: 'forbidden',
      title: 'Запрещено',
      message: fallbackMessage ?? 'Изменение отклонено политикой маршрута',
      affectedSubsystems: [],
      requiresConfirmation: false
    };
  }
  if (contract.requiresConfirmation || contract.policyTier === 'CONFIRMATION_REQUIRED') {
    return {
      status: 'confirmation_required',
      title: 'Требуется подтверждение',
      message: fallbackMessage ?? 'Подтвердите изменение перед сохранением',
      affectedSubsystems: contract.affectedSubsystems ?? [],
      requiresConfirmation: true
    };
  }
  return {
    status: 'allowed',
    title: 'Разрешено',
    message: null,
    affectedSubsystems: contract.affectedSubsystems ?? [],
    requiresConfirmation: false
  };
}

export function mapRejectMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { rejectedFields?: Array<{ message?: string }>; error?: string };
  return d.rejectedFields?.[0]?.message ?? d.error ?? null;
}
